import type { NotificationSummary } from "@/types/notifications";

const INACTIVITY_TOAST_ID = "ticket-inactivity-summary";
const BURST_WINDOW_MS = 500;

type ToastOptions = {
  description?: string;
  duration?: number;
  id?: string | number;
};

interface ToastSink {
  success: (title: string, options?: ToastOptions) => unknown;
  warning: (title: string, options?: ToastOptions) => unknown;
}

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

type LocalizeNotification = (notification: NotificationSummary) => {
  title: string;
  message: string;
};

interface RealtimeNotificationToastBatcherOptions {
  toast: ToastSink;
  t: TranslateFn;
  localize: LocalizeNotification;
  burstWindowMs?: number;
}

const isTicketInactivityNotification = (notification: NotificationSummary) =>
  notification.data?.kind === "ticket_inactivity";

const inactivityNotificationKey = (notification: NotificationSummary) => {
  const ticketId = notification.data?.ticket_id;
  return ticketId == null ? notification.id : String(ticketId);
};

/**
 * Keeps ordinary realtime notifications immediate while coalescing a burst of
 * ticket inactivity events into one stable toast. The notification center
 * still receives every individual record.
 */
export const createRealtimeNotificationToastBatcher = ({
  toast,
  t,
  localize,
  burstWindowMs = BURST_WINDOW_MS,
}: RealtimeNotificationToastBatcherOptions) => {
  const pendingInactivity = new Map<string, NotificationSummary>();
  let flushTimer: number | null = null;

  const flush = () => {
    flushTimer = null;
    const notifications = [...pendingInactivity.values()];
    pendingInactivity.clear();

    if (notifications.length === 0) return;

    if (notifications.length === 1) {
      const localized = localize(notifications[0]);
      toast.warning(localized.title, {
        id: INACTIVITY_TOAST_ID,
        description: localized.message,
        duration: 6000,
      });
      return;
    }

    toast.warning(
      t("notifications_inactive_ticket_summary", {
        count: notifications.length,
        defaultValue: "{{count}} inactive tickets",
      }),
      {
        id: INACTIVITY_TOAST_ID,
        description: t("notifications_inactive_ticket_summary_description", {
          defaultValue:
            "Open notifications to review the tickets that need attention.",
        }),
        duration: 8000,
      }
    );
  };

  const push = (notification: NotificationSummary) => {
    if (!isTicketInactivityNotification(notification)) {
      const localized = localize(notification);
      toast.success(localized.title, {
        description: localized.message,
        duration: 6000,
      });
      return;
    }

    pendingInactivity.set(
      inactivityNotificationKey(notification),
      notification
    );
    if (flushTimer === null) {
      flushTimer = window.setTimeout(flush, burstWindowMs);
    }
  };

  const dispose = () => {
    if (flushTimer !== null) {
      window.clearTimeout(flushTimer);
      flushTimer = null;
    }
    pendingInactivity.clear();
  };

  return { push, flush, dispose };
};
